import { ChangeDetectorRef, Component, Input, OnDestroy, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Representative } from '../../model/representative';
import { Contact } from '../../model/contact';
import { RepresentativeService } from '../../service/data/representative.service';
import { Subscription, finalize, map, take } from 'rxjs';
import { TenantCategory } from '../../model/const/enums';
import { FormGroup } from '@angular/forms';
import { Contract } from '../../model/contract';
import { ContactService } from '../../service/data/contact.service';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from '../../../core/service/message.service';
import { SidebarService } from '../../../core/service/sidebar.service';

@Component({
    selector: 'app-representative-manager',
    templateUrl: './representative-manager.component.html',
    styleUrls: ['./representative-manager.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RepresentativeManagerComponent implements OnDestroy {
  private _subscriptions = new Subscription();
  @ViewChild('representativeFormSidebarContent', { static: false }) representativeFormSidebarContent: TemplateRef<any>;

  constructor(
    private readonly _route: ActivatedRoute,
    public contactDataService: ContactService,
    private readonly _representativeService: RepresentativeService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _messageService: MessageService,
    private readonly _sidebarService: SidebarService
  ) {}

  @Input() dataContext: Contract;
  @Input() form: FormGroup;
  @Input() isBrf: boolean = false;
  representativesFormControl: string;
  representativesLoaded: boolean;
  shouldCallCloseDialog: boolean;

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  // Sidebar processing
  representativeFormValue: Representative;

  openRepresentativeFormSidebar() {
    this._sidebarService.setSidebarContent({ template: this.representativeFormSidebarContent });
    this._sidebarService.toggleSidebar(true);
  }

  closeRepresentativeFormSidebar() {
    if (this.shouldCallCloseDialog) {
      this._messageService
        .showDialog('REPRESENTATIVE.DIALOG.CANCEL_ATTENTION_TEXT')
        .pipe(
          take(1),
          map(res => {
            if (res) {
              this.hideRepresentativeForm();
            }
          })
        )
        .subscribe();
    } else {
      this.hideRepresentativeForm();
    }
  }

  // Update Representative form model from filter
  handleRepresentativeForm(contact?: Contact) {
    const newRepresentative = this._createEmptyFormModel();

    if (contact) {
      // check of representative for current user already exists
      this._subscriptions.add(
        this._representativeService
          .getByContactAndLeaseAgreementId(newRepresentative.leaseAgreementId, contact.id)
          .pipe(
            finalize(() => {
              this.handleEditRepresentativeForm(newRepresentative);
            })
          )
          .subscribe(
            res => {
              // map existed representative to form model
              Object.assign(newRepresentative, res);
            },
            error => {
              // map contact values to representative form
              newRepresentative.contactEmail = contact.email;
              newRepresentative.contactId = contact.id;
              newRepresentative.contactIdentification = contact.contactIdentification;
              newRepresentative.contactName = contact.name;
              newRepresentative.contactPhoneNumber = contact.phone;
              newRepresentative.contactRegistrationKey = contact.registrationKey;
              newRepresentative.contactCategory = contact.category;
              newRepresentative.contactState = contact.state;
              newRepresentative.contactIdentificationType = contact.idType;
            }
          )
      );
    } else {
      this.handleEditRepresentativeForm(newRepresentative);
    }
  }

  // Update Representative form model from rows
  handleEditRepresentativeForm(value?: Representative) {
    this.representativeFormValue = value ? value : this._createEmptyFormModel();
    this.openRepresentativeFormSidebar();
  }

  // After form submitted repeat downloading of representatives
  handleRepresentativeFormSubmitted(): void {
    this._subscriptions.add(
      this._representativeService.getByLeaseAgreementId(this.dataContext.id).subscribe(res => {
        const cachedCultures = this.dataContext.representatives.map(x => ({ id: x.id, cultureName: x.inviteCultureName }));
        for (const cachedCulture of cachedCultures) {
          const existedRes = res.find(x => x.id == cachedCulture.id);
          if (existedRes) {
            existedRes.inviteCultureName = cachedCulture.cultureName;
          }
        }

        this.dataContext.representatives = res;
        this._cdr.detectChanges();
        this.hideRepresentativeForm();
      })
    );
  }

  handleFormStatusChanged(formIsPristine: boolean) {
    if (!formIsPristine) {
      this.shouldCallCloseDialog = true;
    }
  }

  private hideRepresentativeForm() {
    this._sidebarService.toggleSidebar(false);
    this.representativeFormValue = null;
    this.shouldCallCloseDialog = false;
  }

  private _createEmptyFormModel(): Representative {
    var representative = new Representative(this._route.snapshot.paramMap.get('id'));
    representative.faultNotificationsAccess = true;
    representative.contactCategory = TenantCategory.PhysicalPerson;

    return representative;
  }
}
